import { useEffect, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@dental-staffing-platform/auth';
import {
  AppShell,
  Button,
  IconDashboard,
  IconBuilding,
  IconClipboard,
  IconLogOut,
  IconShieldCheck,
  IconUser,
  IconClock,
  IconClose,
  IconMenu,
  navLinkClass,
} from '@dental-staffing-platform/ui';

const NAV = [
  { id: 'overview', label: 'Overview', links: [{ to: '/', label: 'Home', icon: IconDashboard }] },
  {
    id: 'people',
    label: 'People & access',
    links: [
      { to: '/invites', label: 'Invites', icon: IconUser },
      { to: '/credentials', label: 'Credentials', icon: IconShieldCheck },
      { to: '/professionals', label: 'People', icon: IconUser },
    ],
  },
  {
    id: 'staffing',
    label: 'Staffing & finance',
    links: [
      { to: '/operations', label: 'Shifts', icon: IconBuilding },
      { to: '/payments', label: 'Payments', icon: IconClipboard },
      { to: '/payout-onboarding', label: 'Payout onboarding', icon: IconUser },
      { to: '/tax-documents', label: 'Tax documents', icon: IconClipboard },
    ],
  },
  {
    id: 'trust',
    label: 'Trust & support',
    links: [
      { to: '/support', label: 'Support cases', icon: IconClipboard },
      { to: '/review-moderation', label: 'Review moderation', icon: IconShieldCheck },
    ],
  },
  {
    id: 'activity',
    label: 'Activity & system',
    links: [
      { to: '/deliveries', label: 'Message deliveries', icon: IconClipboard },
      { to: '/audit', label: 'Audit', icon: IconClipboard },
      { to: '/system', label: 'System', icon: IconClock },
    ],
  },
];

function initials(email: string): string {
  return (email[0] ?? 'A').toUpperCase();
}

export function AdminLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  useEffect(() => setMobileNavOpen(false), [location.pathname]);
  useEffect(() => {
    if (!mobileNavOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMobileNavOpen(false);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [mobileNavOpen]);
  const isActive = (to: string) =>
    to === '/' ? location.pathname === '/' : location.pathname.startsWith(to);

  const sidebar = (
    <nav
      id="admin-navigation"
      aria-label="Admin navigation"
      className={`dsp-stack dsp-mobile-nav${mobileNavOpen ? ' dsp-mobile-nav--open' : ''}`}
      style={{ gap: 2 }}
    >
      {NAV.map((group) => (
        <section key={group.id} aria-labelledby={`admin-nav-${group.id}`}>
          <h2
            id={`admin-nav-${group.id}`}
            className="dsp-nav-section"
            style={{ display: 'block', margin: 0 }}
          >
            {group.label}
          </h2>
          {group.links.map(({ to, label, icon: Icon }) => (
            <Link key={to} to={to} className={navLinkClass(isActive(to))}>
              <Icon size={18} />
              {label}
            </Link>
          ))}
        </section>
      ))}
      <Button
        variant="ghost"
        size="sm"
        className="dsp-mobile-signout"
        onClick={() => void logout()}
      >
        <IconLogOut size={16} /> Sign out
      </Button>
    </nav>
  );

  const sidebarFooter = (
    <div className="dsp-stack" style={{ gap: 8 }}>
      <div className="dsp-userchip" style={{ width: '100%' }}>
        <span className="dsp-userchip__avatar">{initials(user?.email ?? 'A')}</span>
        <span className="dsp-userchip__name" style={{ flex: 1 }}>
          {user?.email ?? 'Admin'}
        </span>
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => void logout()}
        style={{ justifyContent: 'flex-start' }}
      >
        <IconLogOut size={16} /> Sign out
      </Button>
    </div>
  );

  return (
    <AppShell
      brand={
        <div className="dsp-mobile-brand-row">
          <img src="/dpp-logo.png" alt="Dental Purchasing Partners" className="dsp-rail-logo" />
          <button
            type="button"
            className="dsp-mobile-menu-trigger"
            aria-expanded={mobileNavOpen}
            aria-controls="admin-navigation"
            onClick={() => setMobileNavOpen((open) => !open)}
          >
            {mobileNavOpen ? <IconClose size={18} /> : <IconMenu size={18} />}
            <span>{mobileNavOpen ? 'Close' : 'Menu'}</span>
          </button>
        </div>
      }
      sidebar={sidebar}
      sidebarFooter={sidebarFooter}
      headerRight={
        <div className="dsp-userchip">
          <span className="dsp-userchip__avatar">{initials(user?.email ?? 'A')}</span>
          <span className="dsp-userchip__name">Admin</span>
        </div>
      }
    >
      <Outlet />
    </AppShell>
  );
}
